import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Badge, Box, Button, Group, Select, Stack, Text, TextInput } from '@mantine/core';
import {
  CANONICAL_TARGETS,
  HOSTING_POOL_TYPES,
  HREFLANG_ROLES,
  SITE_TYPE_BY_ENTITY,
} from '@seo/contracts';
import { publicMessageOf, useTRPC } from '../lib/trpc';
import { DeployPanel } from '../components/DeployPanel';
import { useCan } from '../lib/session';
import classes from './EntityPage.module.css';

/**
 * Візард нового сайту — шість кроків (мокап `newSite`).
 *
 * Два виправлення проти прототипу, обидва навмисні:
 *
 * 1. **`stepMap` мокапа зламаний.** `[[0],[1],[2],[5],[3,4],[6]]` розкладає вісім секцій на
 *    шість кроків так, що кроки 3–5 показують чужі секції, а восьма — «Перевірка перед
 *    деплоєм» — не рендериться взагалі. Тобто «Задеплоїти» тиснеться без єдиної перевірки, з
 *    переліком, який автор намалював саме щоб її не тиснули наосліп. Тут це справжній крок 6.
 * 2. **Чернетки немає — є сам сайт.** `builds` не існує без рядка в `sites`, а тримати
 *    чернетку в браузері означало б, що закрита вкладка стирає роботу. Сайт створюється на
 *    третьому кроці, щойно зібрано мінімум, якого вимагає `sites.create`, і далі візард
 *    редагує вже наявний рядок зі статусом `build`. Побічний наслідок — візард можна
 *    покинути й повернутись.
 */

const STEPS = ['Домен', 'Звʼязка', 'ГЕО та мова', 'Шаблон', 'SEO та тех', 'Деплой'] as const;

const LEVEL_COLOR: Record<string, string> = { ok: 'mint', warn: 'amber', fail: 'rose' };
const LEVEL_LABEL: Record<string, string> = { ok: 'ОК', warn: 'ПЕРЕВІРИТИ', fail: 'НЕ ГОТОВО' };

export function NewSitePage(): React.JSX.Element {
  const trpc = useTRPC();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canWrite = useCan('site_seo', 'write');

  /**
   * Домен може прийти з EMD-генератора: там його щойно купили, і передруковувати його руками
   * означало б ризикнути обома — і доменом, і сайтом — через одну літеру.
   */
  const search = useSearch({ from: '/authed/sites/new' });

  const [step, setStep] = useState(1);
  const [siteId, setSiteId] = useState<string | null>(null);
  const [domain, setDomain] = useState(search.domain ?? '');
  const [newPoolName, setNewPoolName] = useState('');
  const [newPoolType, setNewPoolType] = useState<(typeof HOSTING_POOL_TYPES)[number]>('s3');
  const [hostingPoolId, setHostingPoolId] = useState<string | null>(null);
  const [entityType, setEntityType] = useState<'brand' | 'game' | 'cluster'>('brand');
  const [entityId, setEntityId] = useState<string | null>(null);
  const [offerLinkId, setOfferLinkId] = useState<string | null>(null);
  const [geoId, setGeoId] = useState<string | null>(null);
  const [language, setLanguage] = useState<string | null>(null);
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [canonicalTarget, setCanonicalTarget] = useState<string>('self');
  const [hreflangRole, setHreflangRole] = useState<string>('lang');
  const [pairedSiteId, setPairedSiteId] = useState<string | null>(null);
  const [newTrackerName, setNewTrackerName] = useState('');
  const [newTrackerUrl, setNewTrackerUrl] = useState('');
  const [newTemplateName, setNewTemplateName] = useState('');

  const pools = useQuery(trpc.sites.hostingPools.queryOptions());
  const trackers = useQuery(trpc.infra.trackerLinks.list.queryOptions());
  const geos = useQuery(trpc.geo.list.queryOptions({ pagination: { limit: 200, direction: 'asc' } }));
  const brands = useQuery(
    trpc.brands.list.queryOptions({ pagination: { limit: 200, direction: 'asc' } }),
  );
  const clusters = useQuery(
    trpc.clusters.list.queryOptions({ pagination: { limit: 200, direction: 'asc' } }),
  );
  const games = useQuery(
    trpc.games.list.queryOptions({ pagination: { limit: 200, direction: 'asc' } }),
  );
  const templates = useQuery(
    trpc.templates.list.queryOptions({ pagination: { limit: 200, direction: 'asc' } }),
  );
  const siteList = useQuery(
    trpc.sites.list.queryOptions({ pagination: { limit: 200, direction: 'desc' } }),
  );

  /** Мови беруться з ГЕО-пресету: сайт мовою, якої немає в пресеті, `createSite` відхилить. */
  const geoRow = (geos.data?.items ?? []).find((geo) => geo.id === geoId);

  const checklist = useQuery({
    ...trpc.sites.deployChecklist.queryOptions({ id: siteId ?? '' }),
    enabled: siteId !== null && step === 6,
  });

  const create = useMutation(
    trpc.sites.create.mutationOptions({
      onSuccess: async (result) => {
        setSiteId(result.id);
        setStep(4);
        await queryClient.invalidateQueries({ queryKey: trpc.sites.pathKey() });
      },
    }),
  );

  /** Створений трекер одразу стає обраним: інакше оператор додав би його й забув вибрати. */
  /** Створений пул одразу стає обраним — інакше оператор клацає його вдруге в тому ж рядку. */
  const createPool = useMutation(
    trpc.infra.hostingPools.create.mutationOptions({
      onSuccess: async (result) => {
        setNewPoolName('');
        setHostingPoolId(result.id);
        await queryClient.invalidateQueries({ queryKey: trpc.sites.pathKey() });
      },
    }),
  );

  /**
   * Шаблон створюється тут же — з тієї самої причини, що й пул на першому кроці.
   *
   * З порожньої бази перелік шаблонів порожній, а візард нічого про це не каже: селект без
   * варіантів виглядає як «шаблони ще не підвантажились». Оператор, який заводить першу
   * мережу, упирається саме тут — і йому нема куди піти, бо конструктор шаблонів живе на
   * іншому екрані, а візард він тоді втратить.
   *
   * Ті самі два поля, що й на екрані шаблонів: назва й тип сайту. Склад сторінок і блоків
   * лишається в конструкторі — дублювати його в майстрі означало б мати дві правди про те,
   * з чого складається шаблон.
   */
  const createTemplate = useMutation(
    trpc.templates.create.mutationOptions({
      onSuccess: async (result) => {
        setNewTemplateName('');
        setTemplateId(result.id);
        await queryClient.invalidateQueries({ queryKey: trpc.templates.pathKey() });
      },
    }),
  );

  const createTracker = useMutation(
    trpc.infra.trackerLinks.create.mutationOptions({
      onSuccess: async (result) => {
        setOfferLinkId(result.id);
        setNewTrackerName('');
        setNewTrackerUrl('');
        await queryClient.invalidateQueries({ queryKey: trpc.infra.pathKey() });
      },
    }),
  );

  const update = useMutation(
    trpc.sites.update.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: trpc.sites.pathKey() });
      },
    }),
  );

  const entityOptions =
    entityType === 'brand'
      ? (brands.data?.items ?? []).map((row) => ({ value: row.id, label: row.name }))
      : entityType === 'cluster'
        ? (clusters.data?.items ?? []).map((row) => ({ value: row.id, label: row.name }))
        : (games.data?.items ?? []).map((row) => ({ value: row.id, label: row.name }));

  /**
   * Кожен крок називає, чого саме бракує, а не просто гасить кнопку.
   *
   * Неактивна кнопка без причини — найдешевший спосіб зупинити оператора назавжди: він бачить,
   * що далі не йде, і не бачить, що зробити.
   */
  const blocker = ((): string | null => {
    if (step === 1) {
      if (domain.trim().length < 3) return 'Вкажіть домен';
      if (hostingPoolId === null) return 'Оберіть хостинг-пул — без нього виливати нікуди';
      return null;
    }
    if (step === 2) return entityId === null ? 'Оберіть сутність, до якої привʼязаний сайт' : null;
    if (step === 3) {
      if (geoId === null) return 'Оберіть ГЕО';
      if (language === null) return 'Оберіть основну мову з пресету ГЕО';
      return null;
    }
    if (step === 5 && canonicalTarget === 'drop' && pairedSiteId === null) {
      return 'Канонікал у дроп потребує вказаного дропа у звʼязці';
    }
    return null;
  })();

  const goNext = (): void => {
    if (blocker !== null) return;
    if (step === 3 && siteId === null) {
      create.mutate({
        domain: domain.trim(),
        geoId: geoId ?? '',
        language: language ?? '',
        entityType,
        entityId: entityId ?? '',
        hostingPoolId,
      });
      return;
    }
    if (step === 4 && siteId !== null) {
      update.mutate({ id: siteId, patch: { templateId } });
      setStep(5);
      return;
    }
    if (step === 5 && siteId !== null) {
      update.mutate({
        id: siteId,
        patch: {
          canonicalTarget: canonicalTarget as (typeof CANONICAL_TARGETS)[number],
          hreflangRole: hreflangRole as (typeof HREFLANG_ROLES)[number],
          pairedSiteId,
          offerLinkId,
        },
      });
      setStep(6);
      return;
    }
    setStep(Math.min(6, step + 1));
  };

  return (
    <Stack gap="md">
      <Group gap={6} data-testid="wizard-steps">
        {STEPS.map((label, index) => {
          const number = index + 1;
          return (
            <Badge
              key={label}
              size="sm"
              variant={number === step ? 'filled' : 'default'}
              color={number < step ? 'mint' : 'slate'}
              data-testid={`wizard-step-${String(number)}`}
              data-step-state={number === step ? 'current' : number < step ? 'done' : 'todo'}
            >
              {number}. {label}
            </Badge>
          );
        })}
      </Group>

      <Box className={classes.panel} data-testid="wizard-panel">
        {step === 1 ? (
          <Group gap="sm" align="flex-end" wrap="wrap">
            <TextInput
              label="Домен"
              placeholder="baobet-fr.com"
              value={domain}
              disabled={siteId !== null}
              data-testid="wizard-domain"
              style={{ width: 260 }}
              onChange={(event) => {
                setDomain(event.currentTarget.value);
              }}
            />
            <Select
              label="Хостинг-пул"
              searchable
              data={(pools.data ?? []).map((pool) => ({
                value: pool.id,
                label: `${pool.name} (${String(pool.sitesCount)}/${String(pool.capacity)})`,
              }))}
              value={hostingPoolId}
              onChange={setHostingPoolId}
              data-testid="wizard-pool"
              style={{ width: 240 }}
            />
            {/*
             * Пул створюється тут же — так само, як трекер на кроці 4, і з тієї ж причини.
             *
             * `infra.hostingPools.create` існував без жодного викликача з UI, а візард без
             * пулу не пускає далі: з порожньої бази мережу не завести взагалі — ринок і бренд
             * заводяться, а сайт ні, бо пул нема з чого обрати й нема де створити. Знайшов це
             * не гейт «кожна процедура має викликача» (процедура лежала в успадкованому
             * алоулисті), а наскрізний прохід: гейт питає «чи є викликач», прохід — «чи можна
             * цим користуватись».
             */}
            <TextInput
              size="xs"
              label="Новий пул"
              placeholder="hetzner-fr-01"
              value={newPoolName}
              data-testid="wizard-pool-name"
              style={{ width: 170 }}
              onChange={(event) => {
                setNewPoolName(event.currentTarget.value);
              }}
            />
            <Select
              size="xs"
              label="Тип пулу"
              allowDeselect={false}
              data={HOSTING_POOL_TYPES.map((value) => ({ value, label: value }))}
              value={newPoolType}
              data-testid="wizard-pool-type"
              style={{ width: 130 }}
              onChange={(value) => {
                setNewPoolType(value ?? 's3');
              }}
            />
            <Button
              size="compact-xs"
              variant="default"
              loading={createPool.isPending}
              disabled={newPoolName.trim() === ''}
              data-testid="wizard-pool-add"
              onClick={() => {
                createPool.mutate({ name: newPoolName.trim(), type: newPoolType });
              }}
            >
              Додати пул
            </Button>
            {createPool.error === null ? null : (
              <Text className={classes.danger} data-testid="wizard-pool-error" role="alert">
                {publicMessageOf(createPool.error)}
              </Text>
            )}
            <Text className={classes.dim}>
              Тип сайту виводиться з сутності — його не обирають окремо
            </Text>
          </Group>
        ) : null}

        {step === 2 ? (
          <Group gap="sm" align="flex-end" wrap="wrap">
            <Select
              label="Сутність"
              allowDeselect={false}
              data={[
                { value: 'brand', label: 'Бренд' },
                { value: 'game', label: 'Слот' },
                { value: 'cluster', label: 'Кластер' },
              ]}
              value={entityType}
              data-testid="wizard-entity-type"
              style={{ width: 160 }}
              onChange={(value) => {
                setEntityType(
                  value === 'game' || value === 'cluster' || value === 'brand' ? value : 'brand',
                );
                // Сутність іншого типу — інший перелік; лишити старий id означало б привʼязати
                // сайт до бренду, обравши «кластер».
                setEntityId(null);
              }}
            />
            <Select
              label="Запис"
              searchable
              data={entityOptions}
              value={entityId}
              onChange={setEntityId}
              data-testid="wizard-entity"
              style={{ width: 280 }}
            />
            <Select
              label="Офер / трекер"
              searchable
              clearable
              data={(trackers.data ?? []).map((row) => ({ value: row.id, label: row.name }))}
              value={offerLinkId}
              onChange={setOfferLinkId}
              data-testid="wizard-offer"
              style={{ width: 220 }}
            />
            {/*
             * Трекер заводиться тут же, а не «десь у налаштуваннях».
             *
             * `tracker_links` жила без сервісу й без роутера, тобто перший офер довелося б
             * дописувати руками в базу — і саме він вирішує, куди ведуть кнопки CTA всієї
             * сітки. Місце обране за потребою: бракує його рівно тут.
             */}
            <TextInput
              size="xs"
              label="Новий трекер"
              placeholder="Affilka trk-04"
              value={newTrackerName}
              data-testid="wizard-tracker-name"
              style={{ width: 170 }}
              onChange={(event) => {
                setNewTrackerName(event.currentTarget.value);
              }}
            />
            <TextInput
              size="xs"
              label="Шаблон адреси"
              placeholder="https://trk.example/{sub}"
              value={newTrackerUrl}
              data-testid="wizard-tracker-url"
              style={{ width: 220 }}
              onChange={(event) => {
                setNewTrackerUrl(event.currentTarget.value);
              }}
            />
            <Button
              size="compact-xs"
              variant="default"
              loading={createTracker.isPending}
              disabled={newTrackerName.trim() === '' || newTrackerUrl.trim() === ''}
              data-testid="wizard-tracker-add"
              onClick={() => {
                createTracker.mutate({
                  name: newTrackerName.trim(),
                  urlTemplate: newTrackerUrl.trim(),
                });
              }}
            >
              Додати трекер
            </Button>
            {createTracker.error === null ? null : (
              <Text className={classes.danger} data-testid="wizard-tracker-error" role="alert">
                {publicMessageOf(createTracker.error)}
              </Text>
            )}
          </Group>
        ) : null}

        {step === 3 ? (
          <Group gap="sm" align="flex-end" wrap="wrap">
            <Select
              label="ГЕО"
              searchable
              data={(geos.data?.items ?? []).map((geo) => ({
                value: geo.id,
                label: `${geo.countryCode} — ${geo.countryName}`,
              }))}
              value={geoId}
              data-testid="wizard-geo"
              style={{ width: 240 }}
              onChange={(value) => {
                setGeoId(value);
                // Мова належить пресету: збережена від попереднього ГЕО, вона не пройде
                // `assertGeoCarriesLanguage`, і відмова прийшла б аж на створенні.
                setLanguage(null);
              }}
            />
            <Select
              label="Основна мова"
              data={(geoRow?.languages ?? []).map((code) => ({ value: code, label: code }))}
              value={language}
              onChange={setLanguage}
              disabled={geoId === null}
              data-testid="wizard-language"
              style={{ width: 160 }}
            />
            {geoRow === undefined ? null : (
              <Text className={classes.dim} data-testid="wizard-geo-note">
                валюта {geoRow.currencyCode} · {geoRow.minAge}+
              </Text>
            )}
          </Group>
        ) : null}

        {step === 4 ? (
          <Group gap="sm" align="flex-end" wrap="wrap">
            <Select
              label="Шаблон"
              searchable
              clearable
              data={(templates.data?.items ?? []).map((row) => ({
                value: row.id,
                label: `${row.name} · ${String(row.pagesCount)} стор.`,
              }))}
              value={templateId}
              onChange={setTemplateId}
              data-testid="wizard-template"
              style={{ width: 280 }}
            />
            <TextInput
              label="Новий шаблон"
              placeholder="Назва"
              value={newTemplateName}
              onChange={(event) => {
                setNewTemplateName(event.currentTarget.value);
              }}
              data-testid="wizard-template-name"
              style={{ width: 200 }}
            />
            <Button
              size="compact-xs"
              variant="default"
              disabled={newTemplateName.trim().length < 2}
              loading={createTemplate.isPending}
              data-testid="wizard-template-add"
              onClick={() => {
                createTemplate.mutate({
                  name: newTemplateName.trim(),
                  siteType: SITE_TYPE_BY_ENTITY[entityType],
                });
              }}
            >
              Створити шаблон
            </Button>
            <Text className={classes.dim}>
              Склад блоків задається у конструкторі шаблонів, а не тут
            </Text>
            {createTemplate.error === null ? null : (
              <Text className={classes.danger} data-testid="wizard-template-error" role="alert">
                {publicMessageOf(createTemplate.error)}
              </Text>
            )}
          </Group>
        ) : null}

        {step === 5 ? (
          <Group gap="sm" align="flex-end" wrap="wrap">
            <Select
              label="Канонічний"
              allowDeselect={false}
              data={[...CANONICAL_TARGETS]}
              value={canonicalTarget}
              onChange={(value) => {
                setCanonicalTarget(value ?? 'self');
              }}
              data-testid="wizard-canonical"
              style={{ width: 160 }}
            />
            <Select
              label="Роль hreflang"
              allowDeselect={false}
              data={[...HREFLANG_ROLES]}
              value={hreflangRole}
              onChange={(value) => {
                setHreflangRole(value ?? 'lang');
              }}
              data-testid="wizard-hreflang"
              style={{ width: 160 }}
            />
            <Select
              label="Сайт у звʼязці"
              searchable
              clearable
              data={(siteList.data?.items ?? [])
                .filter((row) => row.id !== siteId)
                .map((row) => ({ value: row.id, label: row.domain }))}
              value={pairedSiteId}
              onChange={setPairedSiteId}
              data-testid="wizard-paired"
              style={{ width: 240 }}
            />
          </Group>
        ) : null}

        {step === 6 ? (
          <Stack gap={6} data-testid="wizard-checklist">
            {checklist.data === undefined ? (
              <Text className={classes.dim}>Рахуємо перевірки…</Text>
            ) : (
              <>
                {checklist.data.checks.map((check) => (
                  <Group key={check.key} gap={8} data-testid={`check-${check.key}`}>
                    <Badge size="sm" color={LEVEL_COLOR[check.level] ?? 'slate'}>
                      {LEVEL_LABEL[check.level] ?? check.level}
                    </Badge>
                    <Text size="sm">{check.label}</Text>
                    <Text className={classes.dim}>{check.detail}</Text>
                  </Group>
                ))}
                <Text
                  className={checklist.data.deployable ? classes.dim : classes.danger}
                  data-testid="wizard-deployable"
                >
                  {checklist.data.deployable
                    ? 'Відмов немає — можна збирати й виливати'
                    : 'Є відмови — збірка не піде, поки їх не закрити'}
                </Text>
                {/*
                  * Виливка — термінальна дія мокапового візарда («Задеплоїти сайт»), і до
                  * 15.08 її не було в інтерфейсі взагалі: `deploy.run` існував без викликача.
                  */}
                {siteId === null ? null : (
                  <DeployPanel
                    siteId={siteId}
                    deployable={checklist.data.deployable}
                    testId="wizard-deploy"
                  />
                )}
                {checklist.data.deployable && siteId !== null ? (
                  <Group gap="sm">
                    <Button
                      size="compact-xs"
                      data-testid="wizard-go-build"
                      onClick={() => {
                        void navigate({ to: '/build' });
                      }}
                    >
                      До збірки
                    </Button>
                    <Button
                      size="compact-xs"
                      variant="default"
                      data-testid="wizard-go-site"
                      onClick={() => {
                        void navigate({ to: '/sites/$siteId', params: { siteId } });
                      }}
                    >
                      Відкрити картку
                    </Button>
                  </Group>
                ) : null}
              </>
            )}
          </Stack>
        ) : null}

        {blocker === null ? null : (
          <Text className={classes.warn} mt={8} data-testid="wizard-blocker">
            {blocker}
          </Text>
        )}

        {create.error ?? update.error ? (
          <Text className={classes.danger} mt={8} data-testid="wizard-error" role="alert">
            {publicMessageOf(create.error ?? update.error)}
          </Text>
        ) : null}

        <Group gap="sm" mt="md">
          <Button
            size="compact-xs"
            variant="subtle"
            disabled={step === 1}
            data-testid="wizard-back"
            onClick={() => {
              setStep(Math.max(1, step - 1));
            }}
          >
            Назад
          </Button>
          {step < 6 && canWrite ? (
            <Button
              size="compact-xs"
              loading={create.isPending || update.isPending}
              disabled={blocker !== null}
              data-testid="wizard-next"
              onClick={goNext}
            >
              {step === 3 && siteId === null ? 'Створити сайт' : 'Далі'}
            </Button>
          ) : null}
          {siteId === null ? null : (
            <Text className={classes.dim} data-testid="wizard-created">
              сайт створено — чернетка живе в базі, а не у вкладці
            </Text>
          )}
        </Group>
      </Box>
    </Stack>
  );
}
